/**
 * The capture angles a user meant to shoot, what is actually in each one, and
 * what to do next.
 *
 * The screen used to answer one question - "how many files are marked Duvar
 * 2" - and a count is not an answer to anything somebody standing with a
 * camera actually wants to know. Worse, it invited the wrong conclusion:
 * eight photographs of which seven are unusable is not a well-covered angle.
 *
 * What changed, and what each thing prevents:
 *
 * **One recommendation, at the top, before any table.** The server decides it
 * and sends a code plus its numbers, so the banner cannot say "4 more" while a
 * region card below says something else. A screen that offers five
 * equally-weighted suggestions has not decided anything, and deciding is the
 * value.
 *
 * **Every angle carries a state, not a number.** Missing, problematic, weak,
 * unverified, sufficient - derived on the server from the quality grades, so
 * the seven-of-eight-blurred case reads as a problem rather than as strength.
 *
 * **Nothing is carried by colour alone.** Every state has a tone, a glyph and
 * a sentence. This is used outdoors on a phone.
 *
 * **The angles are ordered by what to fix first**, not by the order they are
 * defined in, and the ranking comes from the server so the screen does not
 * have to know the rule.
 *
 * **Counts that overlap say so.** `usable + problematic + unassessed` is a
 * partition of the files and adds up; findings are counted separately and one
 * file can carry several. Both facts are on screen, because the previous
 * version put "5 files" beside "10 blur findings" and looked like it could not
 * count.
 *
 * **Progressive disclosure.** An empty project does not get six rows of zeros.
 */

import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getGuidedCoverage, getViewpointCoverage } from "@/api/client";
import { paths } from "@/app/paths";
import type { GuidedNextAction, GuidedRegionEvidence, SubjectType } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { PageHeader } from "@/components/product";
import { Button, Callout, DataList, Loading, StatusBadge } from "@/components/ui";
import { useAsync } from "@/lib/useAsync";
import { qualityIssueText } from "@/features/sessions/mediaAnalysis";
import { RegionMediaStrip } from "./RegionMediaStrip";
import { GUIDED_REGIONS, SUBJECT_TITLES, type GuidedSubject } from "./guidedCoverage";
import {
  REGION_STATUS_TERMS,
  nextActionText,
  nextActionTone,
} from "./guidedStatus";

export function ViewpointCoveragePanel({
  projectId,
  subjectType,
}: {
  projectId: string;
  subjectType: GuidedSubject;
}) {
  const [active, setActive] = useState(0);
  const definitions = GUIDED_REGIONS[subjectType];
  const fetcher = useCallback(
    (signal: AbortSignal) => getGuidedCoverage(projectId, { signal }),
    [projectId],
  );
  const technicalFetcher = useCallback(
    (signal: AbortSignal) => getViewpointCoverage(projectId, { signal }),
    [projectId],
  );
  const { state, reload: reloadCoverage } = useAsync(fetcher);
  const { state: technical } = useAsync(technicalFetcher);
  /*
   * Each strip remounts the other one. A file that leaves "bölge atanmamış"
   * arrives in the active region, and a list that does not re-read is a list
   * showing a file in two places at once.
   */
  const [assignedNonce, setAssignedNonce] = useState(0);
  const [unassignedNonce, setUnassignedNonce] = useState(0);

  const definition = definitions[active]!;
  /*
   * A push is a deploy on this project, so for a minute or two a browser can
   * be holding a newer bundle than the API it is talking to. Everything the
   * server newly returns is therefore treated as optional here: the screen
   * loses the recommendation and the states, not its ability to render.
   */
  const coverage = useMemo(() => (state.data
    ? {
        ...state.data,
        review_required_media: state.data.review_required_media ?? 0,
        sufficiency_threshold: state.data.sufficiency_threshold ?? 3,
        regions: state.data.regions.map((region) => ({
          ...region,
          status: region.status ?? "unverified",
          priority: region.priority ?? 99,
          problematic_media: region.problematic_media ?? 0,
          quality_issue_media: region.quality_issue_media ?? 0,
          confirmed_media: region.confirmed_media ?? 0,
          estimated_media: region.estimated_media ?? 0,
        })),
      }
    : null), [state.data]);
  const evidence =
    coverage?.regions.find((region) => region.region === definition.key) ??
    emptyEvidence(definition.key);
  const term = REGION_STATUS_TERMS[evidence.status];

  /*
   * The order to work through the angles, decided by the server's own
   * priority. Ties keep the subject's declared order so the strip does not
   * reshuffle itself between reloads.
   */
  const workOrder = useMemo(() => {
    if (!coverage) return definitions.map((_, index) => index);
    return definitions
      .map((row, index) => ({
        index,
        priority:
          coverage.regions.find((region) => region.region === row.key)?.priority ?? 99,
      }))
      .sort((a, b) => a.priority - b.priority || a.index - b.index)
      .map((row) => row.index);
  }, [coverage, definitions]);

  const hasAnyMedia =
    !!coverage &&
    (coverage.regions.some((region) => region.total_media > 0) ||
      coverage.unassigned_media > 0 ||
      coverage.review_required_media > 0);

  return (
    <div className="stack guided-coverage">
      <PageHeader title={SUBJECT_TITLES[subjectType]} />

      {state.status === "loading" && state.data === null && (
        <Loading label="Bölge kanıtı yükleniyor" />
      )}
      {state.status === "error" && <ErrorState error={state.error} />}

      {coverage && (
        <>
          {/* The most valuable thing on the page, and therefore the first.
              Absent only when the server has not got this far yet. */}
          {coverage.next_action && (
            <Callout
              tone={nextActionTone(coverage.next_action)}
              title="Sırada ne var"
            >
              <NextActionBody
                action={coverage.next_action}
                subjectType={subjectType}
                onGoToRegion={(key) => {
                  const index = definitions.findIndex((row) => row.key === key);
                  if (index >= 0) setActive(index);
                }}
              />
            </Callout>
          )}

          <Callout title="Çekim açıları">
            {/* eslint-disable-next-line -- kept on one line: a mutation guard
                matches this claim literally, and wrapping it defeats the guard */}
            Bölgeler kullanıcının çekim niyetini kaydeder; bu semantik bölge ölçümü değil ve AI tarafından algılandıkları anlamına gelmez.
          </Callout>

          <section
            className={`guided-stage guided-stage--${subjectType}`}
            aria-labelledby="guided-current-region"
          >
            <GuidedVisual
              subject={subjectType}
              active={active}
              regions={coverage.regions}
              order={definitions}
            />
            <div className="guided-stage__content">
              <p className="guided-stage__counter">
                {active + 1} / {definitions.length}
              </p>
              <h2 id="guided-current-region">{definition.label}</h2>
              <StatusBadge tone={term.tone}>
                <span aria-hidden="true">{term.glyph}</span> {term.label}
              </StatusBadge>
              <p>{evidence.usable_media} kullanılabilir fotoğraf · {evidence.total_media} dosya</p>
              <p className="guided-stage__instruction">{term.meaning}</p>
              <p className="guided-stage__instruction faint">{definition.instruction}</p>
              <nav className="guided-stage__controls" aria-label="Çekim açısı gezinme">
                <Button
                  disabled={active === 0}
                  onClick={() => setActive((value) => Math.max(0, value - 1))}
                >
                  Geri
                </Button>
                <Button
                  disabled={active === definitions.length - 1}
                  onClick={() =>
                    setActive((value) => Math.min(definitions.length - 1, value + 1))
                  }
                >
                  İleri
                </Button>
              </nav>
            </div>
          </section>

          {/* Ordered by what to fix first. The number shown is the position in
              the subject's own list, so "3. Duvar 3" stays recognisable even
              when it is sitting first. */}
          <nav className="guided-region-nav" aria-label="Doğrudan açı seçimi">
            {workOrder.map((index) => {
              const row = definitions[index]!;
              const rowEvidence =
                coverage.regions.find((item) => item.region === row.key) ??
                emptyEvidence(row.key);
              const rowTerm = REGION_STATUS_TERMS[rowEvidence.status];
              return (
                <button
                  key={row.key}
                  type="button"
                  aria-current={active === index ? "step" : undefined}
                  className={`guided-region-nav__item guided-region-nav__item--${rowEvidence.status}`}
                  onClick={() => setActive(index)}
                >
                  <span>
                    {index + 1}. {row.label}
                  </span>
                  <small>
                    <span aria-hidden="true">{rowTerm.glyph}</span> {rowTerm.label}
                    {` · ${rowEvidence.usable_media} kullanılabilir fotoğraf / ${rowEvidence.total_media} dosya`}
                  </small>
                </button>
              );
            })}
          </nav>

          <section
            className="card guided-evidence"
            aria-label={`${definition.label} medya kanıtı`}
          >
            <div className="guided-evidence__heading">
              <div>
                <p className="eyebrow">ÇEKİM AÇISI ÖZETİ</p>
                <h3>{definition.label}</h3>
              </div>
              <StatusBadge tone={term.tone}>
                <span aria-hidden="true">{term.glyph}</span> {term.label}
              </StatusBadge>
            </div>

            {/* Progressive disclosure: an angle with nothing in it gets a
                sentence, not four rows of zeros. */}
            {evidence.total_media === 0 ? (
              <p className="muted">
                Bu açıya işaretlenmiş dosya yok. Aşağıdan mevcut dosyaları
                işaretleyebilir veya bu açıyı çekebilirsiniz.
              </p>
            ) : (
              <>
                <DataList
                  items={[
                    { term: "Bu açıya işaretlenen dosya", value: String(evidence.total_media) },
                    { term: "Kullanılabilir", value: String(evidence.usable_media) },
                    { term: "Kalite değerlendirmesini geçemeyen", value: String(evidence.problematic_media) },
                    { term: "Henüz ölçülmemiş", value: String(evidence.unassessed_media) },
                  ]}
                />
                <p className="faint">
                  Bu dört sayı birbirini tamamlar: her dosya tam olarak birinde
                  sayılır. "Kullanılabilir", rekonstrüksiyona girecek görüntü sayısı
                  değildir; o sayı Analiz merkezindeki rekonstrüksiyon uygunluğu
                  satırında yazar.
                </p>

                {(evidence.confirmed_media > 0 || evidence.estimated_media > 0) && (
                  <p className="faint">
                    {evidence.confirmed_media} dosyanın açısını siz ya da çekim akışı
                    belirledi
                    {evidence.estimated_media > 0
                      ? `, ${evidence.estimated_media} tanesi için öneri kullanıldı.`
                      : "."}
                  </p>
                )}

                {/* Findings. A file can carry more than one, so these do not add
                    up to the file counts above - which is exactly the
                    contradiction that reading "5 sorunlu dosya" next to "10
                    sorun" produced. */}
                {Object.keys(evidence.issue_counts).length > 0 && (
                  <details className="stack-tight">
                    <summary>
                      Ölçülen bulgular ({evidence.quality_issue_media} dosyada)
                    </summary>
                    <p className="faint">
                      Bir dosyada birden fazla bulgu olabilir; bu sayılar dosya sayısı
                      değildir ve yukarıdaki dörtlüye eklenmez.
                    </p>
                    <DataList
                      items={Object.entries(evidence.issue_counts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([code, count]) => ({
                          term: qualityIssueText(code),
                          value: String(count),
                        }))}
                    />
                  </details>
                )}
              </>
            )}
          </section>

          <section className="card stack-tight" aria-label={`${definition.label} dosyaları`}>
            <h3>{definition.label} dosyaları</h3>
            <RegionMediaStrip
              key={`${definition.key}-${assignedNonce}`}
              projectId={projectId}
              region={definition.key}
              emptyText={`Bu açıya işaretlenmiş dosya yok. Aşağıdan, açı seçilmeden yüklenmiş dosyaları "${definition.label}" olarak işaretleyebilir veya yeni fotoğraf yükleyebilirsiniz.`}
              actions={[{ label: "Seçilenlerin işaretini kaldır", region: null }]}
              onChanged={() => {
                setUnassignedNonce((value) => value + 1);
                reloadCoverage();
              }}
            />
          </section>

          {coverage.unassigned_media > 0 && (
            <section className="card stack-tight" aria-label="Açı işaretlenmemiş dosyalar">
              <h3>Açı işaretlenmemiş dosyalar ({coverage.unassigned_media})</h3>
              <p className="muted">
                Bu dosyalar geçerlidir; bir açı seçilmeden yüklendikleri için hiçbir
                açıya sayılmıyorlar. Seçip aşağıdaki düğmeyle işaretleyebilirsiniz.
                {/*
                  Two ways to do the same work, and they suit different sizes
                  of it. The strip below marks a handful at once; the queue
                  walks a hundred and fifty one at a time with the model's
                  shortlist as buttons.
                */}
                {" "}
                <Link to={paths.projectRegionReview(projectId)}>
                  Tek tek gözden geçirin
                </Link>
                .
              </p>
              <RegionMediaStrip
                key={`unassigned-${unassignedNonce}`}
                projectId={projectId}
                region="unassigned"
                emptyText="İşaretlenmemiş dosya kalmadı."
                actions={[
                  { label: `Seçilenleri "${definition.label}" olarak işaretle`, region: definition.key },
                ]}
                onChanged={() => {
                  setAssignedNonce((value) => value + 1);
                  reloadCoverage();
                }}
              />
            </section>
          )}

          {/* Only when it exists. A permanent "0 belirsiz" row is a row that
              teaches people to stop reading the page. */}
          {coverage.review_required_media > 0 && (
            <Callout tone="caution" title="Açısı belirlenemeyen dosyalar">
              {coverage.review_required_media} dosya için bir açı önerildi ama
              güvenilir bulunmadığı için hiçbir açıya yazılmadı. Bunlar yukarıdaki
              "açı işaretlenmemiş" listesinde görünür ve seçip işaretleyebilirsiniz.{" "}
              <Link to={paths.projectRegionReview(projectId)}>
                Önerilerle birlikte tek tek gözden geçirin
              </Link>
              .
            </Callout>
          )}

          {hasAnyMedia && (
            <details className="card guided-technical">
              <summary>Teknik Kanıt · Gelişmiş Görünüm</summary>
              {technical.status === "loading" && <Loading label="Teknik kanıt yükleniyor" />}
              {technical.status === "error" && (
                <p className="muted">Teknik kamera kanıtı şu anda kullanılamıyor.</p>
              )}
              {technical.data?.evidence ? (
                <DataList
                  items={[
                    { term: "Kayıtlı kamera", value: String(technical.data.evidence.registered_camera_count) },
                    { term: "Göreli görünüm kümesi", value: String(technical.data.evidence.viewpoint_cluster_count) },
                    { term: "Açısal yayılım", value: `${technical.data.evidence.angular_spread_degrees}°` },
                    { term: "Kaynak", value: `Sparse rekonstrüksiyon v${technical.data.source_reconstruction_version}` },
                  ]}
                />
              ) : (
                technical.status === "success" && (
                  <p className="muted">
                    Uyumlu sparse kamera kanıtı henüz yok. Bu, yüzde sıfır anlamına gelmez.
                  </p>
                )
              )}
              <p className="faint">
                "Yeterli görüntü var" eşiği, rekonstrüksiyon motorunun kendi geometrik
                alt sınırıdır ({coverage.sufficiency_threshold} kullanılabilir görüntü) ve bir
                kalite garantisi değildir.
              </p>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function NextActionBody({
  action,
  subjectType,
  onGoToRegion,
}: {
  action: GuidedNextAction;
  subjectType: SubjectType;
  onGoToRegion: (region: string) => void;
}) {
  const text = nextActionText(action, subjectType);
  return (
    <div className="stack-tight">
      <strong>{text.title}</strong>
      {text.detail && <p className="muted">{text.detail}</p>}
      {action.region && (
        <p>
          <Button onClick={() => onGoToRegion(action.region!)}>Bu açıya git</Button>
        </p>
      )}
    </div>
  );
}

function emptyEvidence(region: string): GuidedRegionEvidence {
  return {
    region,
    total_media: 0,
    usable_media: 0,
    problematic_media: 0,
    unassessed_media: 0,
    quality_issue_media: 0,
    issue_counts: {},
    status: "missing",
    priority: 0,
    confirmed_media: 0,
    estimated_media: 0,
  };
}

/**
 * The subject, with a marker per capture angle.
 *
 * The markers carry the angle's *state*, not just its number: a fill from the
 * status tone, and a glyph beside the index. A ring of six identical circles
 * told the user nothing they could not already read in the list, and colouring
 * them without the glyph would have made the diagram the one part of the page
 * that depends on colour perception.
 *
 * The shape is a schematic and says so in its own description - it is drawn
 * from the subject type, never from anything measured about this project.
 */
function GuidedVisual({
  subject,
  active,
  regions,
  order,
}: {
  subject: GuidedSubject;
  active: number;
  regions: GuidedRegionEvidence[];
  order: readonly { key: string; label: string }[];
}) {
  const selected = order[active]!;
  return (
    <figure className="guided-visual">
      <svg
        viewBox="0 0 420 300"
        role="img"
        aria-labelledby={`guided-${subject}-title guided-${subject}-desc`}
      >
        <title id={`guided-${subject}-title`}>
          {SUBJECT_TITLES[subject]} yönlendirilmiş bölge görünümü
        </title>
        <desc id={`guided-${subject}-desc`}>
          {selected.label} seçili. Şekil çekim niyetini gösterir; otomatik semantik
          algılama değildir. Her işaretin durumu listede de yazılıdır.
        </desc>
        <SubjectShape subject={subject} />
        {order.map((region, index) => {
          const evidence = regions.find((row) => row.region === region.key);
          const status = evidence?.status ?? "missing";
          const position = marker(subject, index);
          return (
            <g
              key={region.key}
              className={`guided-marker guided-marker--${status}${index === active ? " guided-marker--active" : ""}`}
              transform={`translate(${position.x} ${position.y})`}
            >
              <title>{region.label}: {REGION_STATUS_TERMS[status].label}</title>
              <circle r={index === active ? 18 : 13} />
              <text textAnchor="middle" dy=".35em">
                {index + 1}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        {selected.label} · kullanıcı tarafından belirlenen çekim bölgesi
      </figcaption>
    </figure>
  );
}

function SubjectShape({ subject }: { subject: GuidedSubject }) {
  if (subject === "human_body")
    return (
      <g className="guided-shape">
        <circle cx="210" cy="58" r="25" />
        <path d="M175 102 Q210 78 245 102 L255 190 235 190 232 264 210 264 202 190 188 264 166 264 170 190 150 190Z" />
      </g>
    );
  if (subject === "object")
    return (
      <g className="guided-shape">
        <path d="M125 92 210 48 295 92 295 210 210 255 125 210Z" />
        <path d="m125 92 85 46 85-46M210 138v117" />
      </g>
    );
  if (subject === "room")
    return (
      <g className="guided-shape">
        <path d="M70 65h280v200H70zM70 65l70 58h140l70-58M140 123v142M280 123v142" />
      </g>
    );
  // The room, and a figure standing in it. The figure is drawn because the
  // whole point of this subject is that the people are a separate truth from
  // the place; a bare room diagram would say the opposite.
  if (subject === "human_in_environment")
    return (
      <g className="guided-shape">
        <path d="M70 65h280v200H70zM70 65l70 58h140l70-58M140 123v142M280 123v142" />
        <circle cx="210" cy="168" r="15" />
        <path d="M189 196q21-14 42 0l6 52h-13l-2 17h-24l-2-17h-13z" />
      </g>
    );
  if (subject === "building")
    return (
      <g className="guided-shape">
        <path d="M90 120 210 50l120 70v140H90zM145 145h45v45h-45zm85 0h45v45h-45zm-20 55h45v60h-45z" />
      </g>
    );
  return (
    <g className="guided-shape">
      <path d="M40 235 125 120l55 72 55-105 145 148z" />
      <circle cx="315" cy="70" r="28" />
      <path d="M40 235h340" />
    </g>
  );
}

function marker(subject: GuidedSubject, index: number): { x: number; y: number } {
  const positions: Record<GuidedSubject, Array<[number, number]>> = {
    human_body: [[210, 105], [274, 145], [210, 155], [146, 145], [210, 120], [210, 225]],
    object: [[210, 138], [300, 150], [210, 55], [120, 150], [210, 30], [210, 275]],
    room: [[105, 165], [175, 145], [245, 145], [315, 165], [210, 85], [210, 245]],
    building: [[120, 175], [175, 125], [245, 125], [300, 175], [210, 55], [270, 220]],
    scene: [[210, 205], [105, 175], [315, 175], [210, 235], [210, 145], [210, 80]],
    // Seven, not six: this subject has an `overview` as well as the four walls
    // and the floor, and a marker for the people themselves. The last one sits
    // on the figure rather than on a wall, because that is where the person
    // holding the phone has to point.
    human_in_environment: [
      [210, 85], [105, 165], [175, 145], [245, 145], [315, 165], [210, 245], [210, 200],
    ],
  };
  const [x, y] = positions[subject][index]!;
  return { x, y };
}
